/**
 * ENG-5389: scrape Claude Code's "X% of your weekly limit · resets <date>"
 * banner from each Claude Code agent's pane.log and forward to the API as
 * an observation. Wired into the per-agent processing loop in
 * manager-worker.ts; only Claude Code framework agents are checked.
 *
 * "Only on change" semantics:
 *   - Keep an in-memory last-observed value per code_name (pct + reset date).
 *   - Skip the POST when the next banner read matches it.
 *   - This trades a tiny startup-blind window (we don't POST the first
 *     observation after restart if it matches what the server already has)
 *     for a major drop in write volume. The alert path (the API-side
 *     usage-limit-monitor cron, ENG-6183) reads the latest observation per
 *     agent each tick and re-derives the at-limit / approaching state from it,
 *     so the missing duplicate write doesn't change the answer.
 *
 * Per-agent file read is rate-limited to MIN_CHECK_INTERVAL_MS so the
 * manager's normal ~10-30s poll doesn't translate into N file reads/sec for
 * large hosts.
 *
 * ---------------------------------------------------------------------------
 * ENG-8631 — "only on change" was ALSO "only on presence", and that is the bug.
 * ---------------------------------------------------------------------------
 *
 * The sentence above ("the missing duplicate write doesn't change the answer")
 * was true for OPENING an alert and false for CLOSING one, and nobody noticed
 * for months.
 *
 * When the banner DISAPPEARS - which is what recovery looks like from here,
 * because a healthy Claude Code renders no cap line at all - `parseUsageBanner`
 * returned null and this function returned silently. Nothing was POSTed. So the
 * server's "latest observation per agent" stayed frozen at the last SATURATED
 * reading, forever.
 *
 * On the server, `classifyUsage` has exactly one honest recovery branch,
 * `below_threshold`, and it needs a FRESH sub-threshold observation - the thing
 * this file was declining to send. Everything else there is a wall-clock
 * heuristic. And for the saturated banner specifically ("You've hit your weekly
 * limit - resets 4pm (UTC)") the parser yields `reset_precision: 'hour_only'`,
 * which `classifyUsage` branch (d) deliberately excludes from the past-reset
 * carve-out. So an hour_only CRITICAL had ONE exit: the 8-day
 * `stale_observation` fallback, closed as `expired`.
 *
 * Measured cost, 2026-08-24: thirteen agents opened `usage_limit_reached` in
 * ninety minutes and not one of their banner-read timestamps moved across three
 * hourly sweeps. Open criticals went 8 to 22 in four hours on this alert kind
 * alone, several of them against agents that were demonstrably shipping work.
 *
 * TWO CHANGES, and they are separable:
 *
 * 1. REAFFIRM. Re-POST an unchanged banner every REAFFIRM_INTERVAL_MS. Without
 *    this, `observed_at` means "when this value last CHANGED", which is not a
 *    fact anybody downstream wants; with it, `observed_at` means "still true as
 *    of", which is what every reader already assumed it meant. This is also
 *    what makes any future age-based reasoning on the server SAFE - today an
 *    agent genuinely wedged at 100% for three days has a three-day-old
 *    observation, so a server-side staleness rule would close a real alert.
 *
 * 2. CLEAR. After the banner has been absent for ABSENCE_CONFIRMATIONS
 *    consecutive checks, POST a clearing observation (pct 0, source
 *    `pane_log_cleared`). That is a real re-evaluation - a fresh read of the
 *    agent's own pane found no cap - so the server closes it `recovered` rather
 *    than `expired`.
 *
 * WHY THE CLEAR NEEDS CONFIRMING RATHER THAN FIRING ON THE FIRST MISS. ENG-6284
 * established that this banner renders only INTERMITTENTLY, which is why the
 * tail below is 5,000 lines rather than a few dozen. A single miss is therefore
 * weak evidence, and a false clear closes a REAL limit alert - the dangerous
 * direction. Requiring sustained absence keeps the strong signal and still beats
 * the 8-day fallback by two orders of magnitude.
 */

import { bannerRemedyFor, parseUsageBanner } from '@augmented/core';
import { readPaneLogTail } from './persistent-session.js';

// ENG-7908: the spawn marker setupPaneLog() appends to pane.log on every
// (re)spawn, e.g. "--- spawn 2026-07-20T18:19:43.000Z (session agt-scout) ---".
// It is the only reliable epoch boundary in an append-only, timestamp-less
// pane.log, so we use it to scope banner parsing to the CURRENT run (see
// scopeToCurrentPaneEpoch).
const SPAWN_MARKER = /--- spawn \S+ \(session [^)]*\) ---/g;

/**
 * ENG-7908: scope a pane.log tail to the text produced AFTER the most recent
 * spawn marker (the current pane epoch), so a stale weekly-limit banner left in
 * the scrollback from a PREVIOUS Claude Code login can't be re-read as a live
 * cap.
 *
 * pane.log is append-only and carries no per-line timestamps, so a banner from
 * a since-replaced account ("You've hit your weekly limit …") lingers forever in
 * the tail. After an operator logs into a fresh (uncapped) account the agent is
 * respawned to pick up the new credentials (appending a new spawn marker), and
 * the fresh account renders no cap banner, so nothing supersedes the stale one.
 * Parsing only the current epoch drops the stale banner.
 *
 * When the tail contains no marker at all (a very chatty agent whose last spawn
 * scrolled past the window) the whole tail is returned, preserving pre-ENG-7908
 * behaviour for that edge.
 *
 * ENG-8208 note: this survives the marker's deletion. The scoping was introduced
 * to stop a stale banner re-arming the marker, but it guards the OBSERVATION
 * just as much — a banner left in the scrollback by a since-replaced Claude login
 * would otherwise be POSTed as current usage and drive the ENG-6183 alert path
 * off a dead account's numbers.
 */
function scopeToCurrentPaneEpoch(tail: string): string {
  SPAWN_MARKER.lastIndex = 0;
  let lastIndex = -1;
  let match: RegExpExecArray | null;
  while ((match = SPAWN_MARKER.exec(tail)) !== null) {
    lastIndex = match.index;
  }
  if (lastIndex < 0) return tail;
  return tail.slice(lastIndex);
}

const MIN_CHECK_INTERVAL_MS = 60_000;
// ENG-6284: the weekly-limit banner renders only intermittently in the Claude
// Code pane, and a busy agent's pane churns fast (synthetic probes, spinner
// redraws), so a small window almost never contains the banner at sample time —
// the scrape silently produced zero observations and the alert path was starved
// of input (an agent went to 100% with no alert). A wide window reliably catches
// a recent banner. This is cheap: `readPaneLogTail` already reads the whole
// pane.log into memory regardless of the line count — the param only bounds the
// returned slice — so widening it adds no I/O. `parseUsageBanner` returns the
// most-recent banner in the slice, so a window spanning the week's climb still
// reports current usage, not a stale lower pct.
const PANE_TAIL_LINES_FOR_BANNER = 5000;

/**
 * ENG-8631 (1): re-POST an unchanged banner at least this often, so
 * `observed_at` means "still true as of" rather than "when it last changed".
 *
 * 15 minutes, and the volume is negligible because only agents ACTUALLY showing
 * a banner reaffirm: on a 20-agent host with three capped agents that is 12
 * rows an hour, against a table that already absorbs every change event. The
 * far more expensive thing is what it replaces - an operator acting on a
 * three-day-old reading presented as current state.
 */
const REAFFIRM_INTERVAL_MS = 15 * 60_000;

/**
 * ENG-8631 (2): consecutive banner-absent checks required before we POST a
 * clear. Checks are MIN_CHECK_INTERVAL_MS apart, so 30 is ~30 minutes of the
 * banner appearing nowhere in a 5,000-line tail.
 *
 * The asymmetry is deliberate and the direction matters: a missed clear leaves
 * a stale alert open (visible, annoying, already the status quo), while a FALSE
 * clear closes a real cap alert and tells an operator an agent is fine when it
 * cannot work. Thirty minutes of silence buys the second failure mode out
 * cheaply while still being ~380x faster than the 8-day fallback it replaces.
 */
const ABSENCE_CONFIRMATIONS = 30;

interface MonitorState {
  /** Last value successfully POSTed to the API (or read at startup). */
  lastPct: number | null;
  /** ISO of the lastPct's week_resets_at. */
  lastWeekResetsAt: string | null;
  /**
   * CodeRabbit on #4694. The scope of the last POSTed observation. It has to be
   * part of the duplicate key, not just carried alongside it: a banner can flip
   * session <-> weekly while pct and the resolved reset instant both stay put
   * (a saturated cap naming the same hour), and without this the POST is
   * suppressed as a duplicate and the API keeps the previous classification.
   * Since weekly and session drive DIFFERENT alert behaviour, that is a silent
   * mislabel - the exact failure mode this PR exists to end.
   */
  lastLimitScope: string | null;
  /**
   * ENG-10503: the remedy clause last POSTed with the banner, or null. Part of
   * the change check, so a remedy appearing (or changing) on an otherwise
   * identical banner is sent rather than suppressed as a duplicate.
   */
  lastBannerRemedy: string | null;
  /** Last time we actually opened pane.log for this code_name. */
  lastCheckedAt: number;
  /**
   * ENG-8631: when we last successfully POSTed anything for this agent. Drives
   * the reaffirm interval. Null until the first successful POST.
   */
  lastPostedAt: number | null;
  /**
   * ENG-8631: consecutive checks in which the current pane epoch contained no
   * banner. Reset to 0 by any parsed banner. Only counted once we have actually
   * seen one - an agent that has never shown a banner has nothing to clear, and
   * counting it would mean POSTing zeros for the whole healthy fleet.
   */
  consecutiveAbsences: number;
  /**
   * ENG-9634: this process has already posted a clear that was counted from a
   * COLD start - seeded by the control plane's open alert rather than by a
   * banner this process read.
   *
   * It exists to make that exit fire ONCE per episode. The roster flag lags the
   * alert by up to a poll, so without this the agent keeps arriving with
   * `openUsageAlert: true` after the clear lands, counts another 30 absences,
   * and clears again - "re-POSTing zeros every 30 minutes forever", which is
   * precisely what the reset below the clear POST exists to prevent. A test
   * pins it, because the first draft of this change had exactly that bug.
   *
   * Cleared by an actual banner sighting: that starts a genuinely new episode
   * with real evidence behind it. Until then the cold exit stays spent, which
   * fails toward keeping a real alert open - the direction this whole branch
   * has always chosen.
   *
   * Deliberately a separate flag rather than seeding {@link lastPct} with a
   * number. `lastPct` means "a percentage THIS process actually read off the
   * pane" and is what the reaffirm path diffs against; writing a value we never
   * read into it would make a later banner look unchanged and suppress a real
   * reaffirm. Nothing here is ever POSTed as a measurement.
   */
  coldClearPosted: boolean;
}

const state = new Map<string, MonitorState>();

/**
 * Test helper — reset module state. Not for production use.
 */
export function _resetUsageBannerMonitorState(): void {
  state.clear();
}

/**
 * Minimal API surface the monitor needs. Lets the unit test pass a stub
 * without dragging the real api-client transport in.
 */
export interface UsageBannerMonitorApi {
  post(path: string, body: unknown): Promise<unknown>;
}

/**
 * Read this agent's pane.log, parse the weekly-usage banner, and POST an
 * observation if (and only if) it differs from the last value we saw.
 *
 * Errors are swallowed and reported to `log`. This is a soft observability
 * path; an error here must not break the manager poll cycle.
 */
export async function maybeReportUsageBanner(args: {
  api: UsageBannerMonitorApi;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /**
   * ENG-9634: does the CONTROL PLANE currently hold an open
   * `usage_limit_reached` alert for this agent? Supplied by the manager from
   * the `/host/agents` roster it already polls, so this costs no extra call.
   *
   * It exists to re-seed what a manager restart throws away. See the cold-start
   * branch below for why absence alone could never do it.
   */
  openUsageAlert?: boolean;
}): Promise<void> {
  const { api, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }

  let tail: string | null;
  try {
    tail = readPaneLogTail(codeName, PANE_TAIL_LINES_FOR_BANNER);
  } catch (err) {
    log(`[usage-banner] readPaneLogTail failed for '${codeName}': ${(err as Error).message}`);
    return;
  }

  // Always record the check timestamp so a missing banner doesn't make us
  // re-read the file on every tick.
  const next: MonitorState = {
    lastPct: existing?.lastPct ?? null,
    lastWeekResetsAt: existing?.lastWeekResetsAt ?? null,
    lastLimitScope: existing?.lastLimitScope ?? null,
    lastBannerRemedy: existing?.lastBannerRemedy ?? null,
    lastCheckedAt: nowMs,
    lastPostedAt: existing?.lastPostedAt ?? null,
    consecutiveAbsences: existing?.consecutiveAbsences ?? 0,
    coldClearPosted: existing?.coldClearPosted ?? false,
  };

  // ENG-7908: only trust banners from the CURRENT pane epoch (after the last
  // spawn marker). A stale cap banner from a since-replaced Claude login lingers
  // in the append-only pane.log; scoping to the current run drops it.
  const scoped = tail ? scopeToCurrentPaneEpoch(tail) : null;
  const observation = scoped ? parseUsageBanner(scoped, now) : null;

  if (!observation) {
    // ENG-8631: absence is EVIDENCE, and throwing it away is what pinned every
    // hour_only CRITICAL open for eight days. But only for an agent we have
    // actually seen a banner from - `lastPct === null` means either a healthy
    // agent that never had one (nothing to clear) or a manager that restarted
    // and lost its memory (see the note below), and POSTing zeros in either case
    // would be inventing an observation rather than reporting one.
    //
    // A RESTART is deliberately treated as "no opinion" rather than "clear".
    // The in-memory map does not survive it, so after a restart we cannot tell
    // "the banner went away while we were down" from "there was never one".
    // Staying silent leaves any open alert exactly as it was and lets the next
    // genuine banner (or the next sustained absence, once we have re-seen one)
    // decide - which fails toward keeping a real alert open.
    //
    // ENG-9634 REOPENS THAT DECISION, because the escape hatch the comment
    // above relies on does not exist. It says the next genuine banner "or the
    // next sustained absence, once we have re-seen one" will decide - but for a
    // cap that has ALREADY LIFTED there is no banner left to re-see, so
    // `lastPct` can never become non-null and this branch returns silently
    // forever. Measured: four agents across four orgs held CRITICALs open for
    // 5-8 days, and Petra's finally closed on the 8-day `stale_observation`
    // timer - a timeout, which is precisely what ENG-8631 shipped to replace.
    // Worse, a human closing one by hand did not stick: the monitor re-raised
    // it from the same stale row six minutes later.
    //
    // The fix is to restore the missing premise rather than to weaken the
    // guard. `openUsageAlert` says the CONTROL PLANE holds an open cap alert
    // for this agent, which means a banner WAS observed - by a previous
    // incarnation of this process. That is a real observation we lost, not an
    // invented one, so counting absences against it is legitimate and the
    // eventual clear is still evidence: 30 consecutive re-reads of the agent's
    // own pane finding no banner.
    //
    // The fail-safe keeps its teeth exactly where it had them: an agent with NO
    // open alert and no banner still says nothing at all, which is the healthy
    // fleet and the whole reason the guard exists.
    if (!existing || existing.lastPct === null) {
      // No open alert to explain the absence: the healthy fleet. Say nothing,
      // exactly as before.
      if (!args.openUsageAlert) {
        state.set(codeName, next);
        return;
      }
      // Already spent this episode's cold exit. See `coldClearPosted`.
      if (next.coldClearPosted) {
        state.set(codeName, next);
        return;
      }
      if ((existing?.consecutiveAbsences ?? 0) === 0) {
        log(
          `[usage-banner] '${codeName}': no banner in the current pane epoch, but the ` +
            'control plane holds an open usage_limit_reached alert; counting absences ' +
            'from cold so the clear can be posted',
        );
      }
    }
    next.consecutiveAbsences = (existing?.consecutiveAbsences ?? 0) + 1;
    if (next.consecutiveAbsences < ABSENCE_CONFIRMATIONS) {
      state.set(codeName, next);
      return;
    }
    try {
      await api.post('/host/usage-observations', {
        agent_id: agentId,
        // A cleared banner is not "0% used" - we do not know the number, we
        // know the CAP is gone. 0 is the only value the route accepts that the
        // server will classify below any threshold, and `source` below is what
        // makes the row self-describing rather than a fabricated measurement.
        observed_pct: 0,
        // Carry the last known window so the row stays coherent with the one it
        // supersedes. The server short-circuits on `source` before it reasons
        // about this instant, so it is provenance, not an input to the decision.
        week_resets_at: existing?.lastWeekResetsAt ?? now.toISOString(),
        reset_precision: 'unknown',
        limit_scope: existing?.lastLimitScope ?? 'unknown',
        // ENG-8631: the server keys its `banner_cleared` recovery off exactly
        // this string. `source` is a free-form text column, so this needs no
        // migration and an older API simply stores it and behaves as before.
        source: 'pane_log_cleared',
      });
      log(
        `[usage-banner] '${codeName}': cap banner absent for ${next.consecutiveAbsences} ` +
          'consecutive checks; posted a clearing observation',
      );
      // Back to "we have never seen a banner" so we clear ONCE per episode
      // rather than re-POSTing zeros every 30 minutes forever after.
      next.lastPct = null;
      next.lastWeekResetsAt = null;
      next.lastLimitScope = null;
      next.lastBannerRemedy = null;
      // ENG-9634: mark the cold exit spent. `lastPct` is being set back to null
      // on the line above, so without this the very next poll would re-enter
      // the cold branch (the roster still says the alert is open for another
      // poll or two), count 30 more absences and clear again, forever.
      next.coldClearPosted = true;
      next.consecutiveAbsences = 0;
      next.lastPostedAt = nowMs;
    } catch (err) {
      // Leave consecutiveAbsences AT the threshold (not reset), so the next
      // check retries the clear instead of restarting a 30-minute count.
      log(
        `[usage-banner] clearing POST failed for '${codeName}': ${(err as Error).message}`,
      );
    }
    state.set(codeName, next);
    return;
  }

  // A banner is present: any absence streak was not a recovery.
  next.consecutiveAbsences = 0;
  // ENG-9634: a real sighting starts a genuinely new episode, with evidence this
  // process read itself, so the cold exit is available again for it.
  //
  // Belt-and-braces, and said plainly rather than left to look load-bearing: no
  // test can currently fail on this line, because a sighting also sets
  // `lastPct`, and the cold branch is only reachable while `lastPct` is null.
  // It is kept so the flag's meaning stays "spent for the CURRENT episode"
  // rather than quietly becoming "spent for this process", which is what it
  // would drift into the first time the gate above is restructured.
  next.coldClearPosted = false;

  const weekResetsAtIso = observation.weekResetsAt.toISOString();
  // ENG-10503: the banner's own remedy clause ("run /usage-credits to ask your
  // admin for a higher limit"), verbatim, for the customer notice to quote.
  // Read from the SAME scoped text the parser used, and attached only when it
  // belongs to the exact banner OCCURRENCE the parser chose (CodeRabbit on
  // #6057), so an older banner's remedy is never sent with a newer banner that
  // carried none, whatever its kind. `now` is the parser's own reference, so the
  // two agree on which banners are valid. Null when the banner carried none:
  // never synthesised.
  const bannerRemedy = bannerRemedyFor(scoped ?? '', observation.limitQualifier, now);
  const unchanged =
    existing !== undefined &&
    existing.lastPct === observation.pct &&
    existing.lastWeekResetsAt === weekResetsAtIso &&
    existing.lastLimitScope === observation.limitScope &&
    existing.lastBannerRemedy === bannerRemedy;
  // ENG-8631: an unchanged banner still gets re-POSTed once per reaffirm
  // interval. `lastPostedAt === null` means we have never posted this value
  // (the startup-blind window the header describes), and posting it is
  // strictly better than the pre-ENG-8631 silence.
  const dueToReaffirm =
    next.lastPostedAt === null || nowMs - next.lastPostedAt >= REAFFIRM_INTERVAL_MS;
  if (unchanged && !dueToReaffirm) {
    state.set(codeName, next);
    return;
  }

  try {
    await api.post('/host/usage-observations', {
      agent_id: agentId,
      observed_pct: observation.pct,
      week_resets_at: weekResetsAtIso,
      // ENG-8901: say whether that instant was READ off the banner or
      // RECONSTRUCTED from an hour with the day invented. Without this the
      // server cannot tell a measurement from a guess, which is how a reset
      // three days early reached an operator as a precise timestamp.
      reset_precision: observation.resetPrecision,
      // ENG-9007 wired the producer end. `classifyLimitScope` had been reading
      // the banner's qualifier and `parseUsageBanner` had been returning it on
      // every observation, but this POST never carried it — so the API's
      // validate-or-'unknown' contract (host-runtime.ts: an older host that
      // does not send it is not an error) resolved EVERY observation on the
      // fleet to `limit_scope: 'unknown'`, and every usage alert rendered
      // "banner qualifier not recognised, treated as weekly".
      //
      // That sentence was false on its face for the commonest banner of all —
      // "You've used 93% of your weekly limit", whose qualifier the parser
      // hard-codes to 'weekly'. Nothing was unrecognised; nobody had asked.
      // Sending the field is the whole fix: a session cap can now be told from
      // a weekly one, which is what ENG-9007 exists to do.
      limit_scope: observation.limitScope,
      source: 'pane_log',
      // ENG-10503: the key is OMITTED when there is no remedy, not sent as null,
      // so "the banner carried none" can never arrive looking like a value.
      ...(bannerRemedy ? { banner_remedy: bannerRemedy } : {}),
    });
    next.lastPct = observation.pct;
    next.lastWeekResetsAt = weekResetsAtIso;
    // Only after a SUCCESSFUL POST, matching the two fields above: on failure
    // the state stays at its previous value so the next tick retries.
    next.lastLimitScope = observation.limitScope;
    next.lastBannerRemedy = bannerRemedy;
    // ENG-8631: stamp the reaffirm clock only on success, for the same reason —
    // a failed POST must not start a fresh 15-minute silence.
    next.lastPostedAt = nowMs;
    state.set(codeName, next);
  } catch (err) {
    log(`[usage-banner] POST /host/usage-observations failed for '${codeName}': ${(err as Error).message}`);
    // Leave the in-memory state at its previous value so the next tick
    // retries the POST. lastCheckedAt is still updated so we don't hammer
    // the API at every tick on persistent failure.
    state.set(codeName, next);
  }
}
